import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Wrench, Rocket } from 'lucide-react';

const steps = [
  {
    id: 'discovery',
    number: '01',
    icon: MessageSquare,
    title: 'Discovery Call',
    description: 'We start with a free consultation to understand your vision, goals, and requirements. No commitment, just conversation.',
  },
  {
    id: 'build',
    number: '02',
    icon: Wrench,
    title: 'We Build & Iterate',
    description: 'Our team designs and develops your solution using agile methodology. You\'re involved every step of the way with regular updates.',
  },
  {
    id: 'launch',
    number: '03',
    icon: Rocket,
    title: 'Launch & Scale',
    description: 'We deploy your solution and provide ongoing support to ensure success. As your business grows, we help you scale.',
  },
];

const HowItWorks = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section
      id="process"
      className="relative py-20 md:py-32 overflow-hidden"
      data-testid="how-it-works-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#030305]" />
      <div className="absolute inset-0 grid-bg opacity-20" />
      
      {/* Gradient accent */}
      <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-cyan-500/10 rounded-full blur-[200px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-24"
        >
          <span className="text-cyan-400 text-sm font-mono tracking-wider uppercase mb-4 block">
            Our Process
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="process-heading">
            How It Works
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            A simple, transparent process to bring your software ideas to life. 
            From the first call to launch and beyond.
          </p>
        </motion.div>

        {/* Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12"
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.id}
                variants={itemVariants}
                className={`relative ${index < steps.length - 1 ? 'process-connector' : ''}`}
                data-testid={`process-step-${step.id}`}
              >
                <div className="text-center">
                  {/* Step number */}
                  <div className="relative inline-block mb-6">
                    <span className="text-7xl md:text-8xl font-bold text-white/5 absolute -top-4 -left-4">
                      {step.number}
                    </span>
                    <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 border border-white/10 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                      <Icon className="w-10 h-10 text-violet-400" strokeWidth={1.5} />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl md:text-2xl font-semibold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-xs mx-auto">
                    {step.description}
                  </p>
                </div>

                {/* Connector arrow (hidden on mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 -right-6 z-10">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-violet-500/50">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
